// WizardRunner — reusable Step Wizard orchestrator.
//
// Drives the same Step 1 / 2 / 3 flow used by both the LIFF wizard
// (`ScanWizardPage`) and the public scan wizard
// (`ScanWizardPublicPage` in its finalized state). Auth + endpoint
// paths come in via `ctx: WizardCtx` (see types.ts), so the same
// component works under either JWT (LIFF) or scan_session (public
// scan) — see liffWizardCtx / scanWizardCtx helpers in types.ts.
//
// Responsibilities:
//   - load the current submission via ctx.paths.getSubmission
//   - resume at the first incomplete required step
//   - per-step render slots (the parent supplies the actual step
//     components — WizardRunner doesn't know about LIFF / scan and
//     doesn't import them directly)
//   - auto-advance to the next submission when status flips to
//     submitted / skipped
//   - skip flow (POST .../skip via ctx)
//   - "all done" callback when the last submission finalizes
//
// Spec: docs/specs/location-tasks.md §8.3 (Step Wizard UI).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowRight, ListChecks } from "lucide-react";
import { Card, CardHeader, MobileSpinner, EmptyMobile, PrimaryButton } from "../../../liff/components/Mobile.js";
import { apiFetch } from "../../../lib/api-client.js";
import StepProgress from "./StepProgress.js";
import SkipModal from "./SkipModal.js";
import Step1Verify from "./Step1Verify.js";
import Step2Evidence from "./Step2Evidence.js";
import Step3Sign from "./Step3Sign.js";
import { stepCompleted, stepRequired, type WizardCtx, type WizardData, type WizardStep } from "./types.js";

/** Render-slot signature — caller supplies the actual step UI.
 *  Lets WizardRunner stay agnostic to whether the LIFF version's
 *  StepDeviceVerify / scan version's no-op placeholder is plugged in. */
export type StepSlot = (args: { data: WizardData; onComplete: () => void }) => React.ReactElement;

/** Text response slot — different shape because text is held by
 *  WizardRunner (controlled), not by the step component. The step is
 *  presentation-only: render a textarea bound to `text` + propagate
 *  changes via `onTextChange`. NO save button, NO PATCH inside the
 *  step — handleSubmit batches the text into the final
 *  `{ status: 'submitted', text_response }` PATCH. */
export type TextStepSlot = (args: {
  text: string;
  onTextChange: (next: string) => void;
}) => React.ReactElement;

export interface WizardRunnerProps {
  ctx: WizardCtx;
  /** Submission IDs to walk through, in display order. Empty list
   *  short-circuits to an EmptyMobile saying nothing to do. */
  submissionIds: string[];
  /** Per-sub-step renderers. Step 2/3 slots typically pass the
   *  shared step components (StepTextResponse, StepCameraEvidence,
   *  StepFileEvidence, StepCanvasSignature) wrapped with the
   *  current ctx. Step 1 slots are LIFF-only today; public scan
   *  callers pass null/undefined and Step 1 falls back to "尚未上線"
   *  placeholders (verify_* fields are already populated by
   *  /scan/finalize so Step 1 auto-completes in practice). */
  renderDevice?: StepSlot;
  renderFace?:   StepSlot;
  renderGeo?:    StepSlot;
  /** Text step uses the controlled-text slot signature — see TextStepSlot. */
  renderText?:   TextStepSlot;
  renderCamera?: StepSlot;
  renderFile?:   StepSlot;
  renderCanvas?: StepSlot;
  /** Render the wizard chrome — title + subtitle + footer (next/prev).
   *  Defaults to a flat layout for the public scan; the LIFF caller
   *  wraps in MobileShell with its own footer. */
  chrome?: (args: {
    title: string;
    subtitle: string;
    body: React.ReactNode;
    footer: React.ReactNode;
  }) => React.ReactElement;
  /** Fires when the LAST submission's last step completes (or skip on
   *  last submission). Caller decides what to render — LIFF closes the
   *  window, public scan shows a "完成" splash. */
  onAllDone?: () => void;
  /** Show this when all submissions are processed. Use cases differ
   *  (LIFF: history link + close; scan: thanks message). Caller-supplied
   *  to avoid coupling. */
  doneScreen?: React.ReactElement;
}

export default function WizardRunner({
  ctx, submissionIds,
  renderDevice, renderFace, renderGeo,
  renderText, renderCamera, renderFile, renderCanvas,
  chrome, onAllDone, doneScreen,
}: WizardRunnerProps): React.ReactElement {
  const [idx, setIdx] = useState(0);
  const [data, setData] = useState<WizardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<WizardStep>(1);
  const [skipOpen, setSkipOpen] = useState(false);
  const [skipBusy, setSkipBusy] = useState(false);
  const [done, setDone] = useState(false);
  /** Text response held locally — only persisted to the server when
   *  handleSubmit fires, per the「文字只是資料、提交時才存」design.
   *  Seeded from data.submission.text_response on each submission load
   *  so resuming an in_progress submission keeps prior text visible. */
  const [pendingText, setPendingText] = useState<string>("");

  const currentId = submissionIds[idx] ?? null;

  const reload = useCallback(async () => {
    if (!currentId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await apiFetch(ctx.paths.getSubmission(currentId), {}, ctx.auth);
      if (!r.ok) {
        const t = await r.text().catch(() => "");
        setError(t || `HTTP ${r.status}`);
        setData(null);
        return;
      }
      const j = (await r.json()) as WizardData;
      setData(j);
      // Seed pendingText from server (handles resume + cross-submission
      // navigation). When advancing to a fresh in_progress submission
      // text_response is null → "".
      setPendingText(j.submission.text_response ?? "");
      setStep(pickStep(j));
    } catch (e) {
      setError((e as Error).message ?? "load_failed");
    } finally {
      setLoading(false);
    }
    // ctx is constructed fresh each render but its semantic identity
    // is stable through a wizard run; depend on the cheap-to-compare
    // submission id instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);

  useEffect(() => { void reload(); }, [reload]);

  // Auto-advance once a submission finalizes. Without this the user
  // would need to manually tap "下一個任務".
  useEffect(() => {
    if (!data) return;
    if (data.submission.status === "submitted" || data.submission.status === "skipped") {
      const t = setTimeout(() => { void advanceSubmission(); }, 600);
      return () => clearTimeout(t);
    }
    // advanceSubmission is stable enough — re-running on data change is the intent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.submission.status]);

  const advanceSubmission = async (): Promise<void> => {
    if (idx + 1 >= submissionIds.length) {
      setDone(true);
      onAllDone?.();
      return;
    }
    setIdx((n) => n + 1);
  };

  const onSkip = async (reason: string): Promise<void> => {
    if (!data) return;
    setSkipBusy(true);
    try {
      const r = await apiFetch(
        ctx.paths.skipSubmission(data.submission.id),
        { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ reason }) },
        ctx.auth,
      );
      if (!r.ok) {
        const t = await r.text().catch(() => "");
        throw new Error(t || `HTTP ${r.status}`);
      }
      setSkipOpen(false);
      await advanceSubmission();
    } finally {
      setSkipBusy(false);
    }
  };

  const requiredSteps = useMemo<WizardStep[]>(() => {
    if (!data) return [];
    return ([1, 2, 3] as WizardStep[]).filter((s) => stepRequired(data.task, s));
  }, [data]);
  const lastStep = requiredSteps[requiredSteps.length - 1] ?? 3;
  const allComplete = data ? requiredSteps.every((s) => stepCompleted(data, s, { pendingText })) : false;
  /** Has pending text that hasn't been persisted to the server yet?
   *  Used by handleSubmit to decide whether to include text_response in
   *  the final PATCH (avoid overwriting server with same value). */
  const textDirty = data ? (pendingText !== (data.submission.text_response ?? "")) : false;

  // Auto-advance: once the current step has all its required evidence /
  // verifications in `data`, jump straight to the next required step.
  // Removes the per-step manual「下一步」click — operator's "I'm done"
  // signal is already the completion of the step's own input (photo
  // uploaded, signature drawn, etc.). The final step still needs an
  // explicit submit press so a stray motion right after signing
  // doesn't auto-fire 提交.
  useEffect(() => {
    if (!data) return;
    if (allComplete) return;
    const stepDone = stepRequired(data.task, step) ? stepCompleted(data, step) : true;
    if (!stepDone) return;
    const next = requiredSteps.find((s) => s > step);
    if (next) setStep(next);
  }, [data, step, requiredSteps, allComplete]);

  const handleSubmit = (): void => {
    if (!allComplete || !data) return;
    // 唯一的「提交」入口 — 把 submission status 從 in_progress 翻成
    // submitted。順帶把 pendingText（locally-held 文字回應）一起寫
    // 進去。其他 step 元件（camera / file / signature）只寫對應的
    // r2_key 不碰 status — handleSubmit 是 sole transition 點。
    //
    // text_response 只在 dirty 時才放進 PATCH body，避免無意義 server
    // round-trip 寫同樣的值。
    //
    // Server 端會驗證所有 task.requires_* 欄位都備齊才允許 status 翻
    // submitted；前端的 allComplete gate 跟 server 同源（都看
    // stepRequired / stepCompleted），實務上不該 fail，但仍保 try/catch
    // 處理 race（cron expire 剛好在 PATCH 前掃掉）。
    void (async () => {
      const body: Record<string, unknown> = { status: "submitted" };
      if (textDirty) body.text_response = pendingText;
      try {
        const r = await apiFetch(
          ctx.paths.patchSubmission(data.submission.id),
          {
            method: "PATCH",
            headers: { "content-type": "application/json" },
            body: JSON.stringify(body),
          },
          ctx.auth,
        );
        if (!r.ok) {
          await reload();
          return;
        }
      } catch {
        await reload();
        return;
      }
      await advanceSubmission();
    })();
  };

  // ── Render ────────────────────────────────────────────────────────────

  if (submissionIds.length === 0) {
    return chrome
      ? chrome({ title: "任務", subtitle: "", body: <EmptyMobile title="無待執行任務" body="本次掃描沒有產生任務提交。" />, footer: null })
      : <EmptyMobile title="無待執行任務" body="本次掃描沒有產生任務提交。" />;
  }
  if (loading) return <MobileSpinner message="載入任務中…" />;
  if (error) {
    return (
      <EmptyMobile title="載入失敗" body={error} />
    );
  }
  if (done && doneScreen) return doneScreen;
  if (!data) return <EmptyMobile title="找不到 submission" body="可能已過期或不屬於您。" />;

  const stepBody =
    step === 1 ? <Step1Verify
                   data={data}
                   onChanged={reload}
                   renderDevice={renderDevice}
                   renderFace={renderFace}
                   renderGeo={renderGeo}
                 /> :
    step === 2 ? <Step2Evidence
                   data={data}
                   onChanged={reload}
                   renderText={renderText
                     ? () => renderText({ text: pendingText, onTextChange: setPendingText })
                     : undefined}
                   textHasContent={pendingText.trim().length > 0}
                   renderCamera={renderCamera}
                   renderFile={renderFile}
                 /> :
                 <Step3Sign
                   data={data}
                   onChanged={reload}
                   renderCanvas={renderCanvas}
                 />;

  const canSkip = data.task.allow_skip === 1;
  const isLastStep = step === lastStep;

  const body = (
    <>
      <Card>
        <div className="flex items-center justify-between mb-2">
          <CardHeader title="進度" hint={`Step ${step}/3`} />
          {canSkip && data.submission.status === "in_progress" && (
            <button onClick={() => setSkipOpen(true)} className="text-[10px] uppercase tracking-wider text-warning hover:underline">
              無法完成？
            </button>
          )}
        </div>
        <StepProgress data={data} current={step} />
      </Card>

      {data.task.description_short && (
        <Card>
          <CardHeader title="任務說明" />
          <p className="text-sm text-text-secondary leading-relaxed whitespace-pre-wrap">{data.task.description_short}</p>
        </Card>
      )}

      {stepBody}

      {isLastStep && allComplete && (
        <Card className="bg-success/10 border-success/30">
          <p className="text-xs text-success inline-flex items-center gap-1">
            <ListChecks className="h-3.5 w-3.5" />任務已備齊，按下方按鈕送出。
          </p>
        </Card>
      )}

      <SkipModal
        open={skipOpen}
        taskName={data.task.name}
        busy={skipBusy}
        onCancel={() => setSkipOpen(false)}
        onConfirm={onSkip}
      />
    </>
  );

  // Footer is single-purpose now: a primary submit button that only
  // appears when the operator has finished every required step. The
  // per-step「上一步 / 下一步」chrome went away in favour of auto-
  // advance — the only manual action left is the final submission
  // press, which we intentionally keep so a stray motion right after
  // signing doesn't fire 提交.
  const footer = (allComplete) ? (
    <PrimaryButton onClick={handleSubmit}>
      <span className="inline-flex items-center justify-center gap-1">
        {idx + 1 >= submissionIds.length ? "完成全部" : "下一個任務"}
        <ArrowRight className="h-4 w-4" />
      </span>
    </PrimaryButton>
  ) : null;

  const title = data.task.name;
  const subtitle = `任務 ${idx + 1}/${submissionIds.length}${data.location?.label ? ` · ${data.location.label}` : ""}`;

  if (chrome) return chrome({ title, subtitle, body, footer });
  return (
    <div className={"w-full max-w-lg mx-auto space-y-3 px-3 " + (footer ? "pb-24" : "pb-4")}>
      <div className="text-center">
        <p className="text-sm font-medium text-text-primary">{title}</p>
        <p className="text-xs text-text-muted">{subtitle}</p>
      </div>
      {body}
      {footer && (
        <div className="fixed inset-x-0 bottom-0 p-3 bg-bg-base border-t border-border-subtle">
          <div className="max-w-lg mx-auto">{footer}</div>
        </div>
      )}
    </div>
  );
}

function pickStep(d: WizardData): WizardStep {
  for (const s of [1, 2, 3] as WizardStep[]) {
    if (stepRequired(d.task, s) && !stepCompleted(d, s)) return s;
  }
  return ([1, 2, 3] as WizardStep[]).find((s) => stepRequired(d.task, s)) ?? 1;
}
